import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import {
  api,
  APIError,
  isFeasibilityDetail,
  isQuotaError,
  type AnalysisReport,
  type ChatMessage,
  type ChatProposal,
  type ContentLang,
  type Course,
  type DetectedFlaw,
  type PerILOAnalysis,
  type SuggestedAT,
  type SuggestedILO,
  type SuggestedTLA,
} from "@/api/client";
import FlawFix from "@/components/Audit/FlawFix";
import CreditCost from "@/components/CreditCost";
import DidaktikChat from "@/components/DidaktikChat";
import Header from "@/components/Layout/Header";
import LoadingOverlay from "@/components/Loading";
import QuotaExhaustedCard from "@/components/QuotaExhaustedCard";

// Bender-Tiefenstufen sind Domain-Werte aus dem Backend (deutsch, DB-Enum) —
// bewusst NICHT übersetzt (Pendant: lib/depth.ts).
const BENDER_LABELS = ["Wissen", "Verstehen", "Können", "Performanz"];

async function downloadPdf(courseId: string, reportId: string, courseTitle: string) {
  const token = localStorage.getItem("ca_access_token");
  const res = await fetch(api.exportPdfUrl(courseId, reportId), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error(String(res.status));
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const slug = courseTitle.replace(/[^a-zA-Z0-9]/g, "_").slice(0, 50);
  a.download = `${slug}_audit.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function AuditReport() {
  const { t, i18n } = useTranslation();
  const { id, reportId } = useParams<{ id: string; reportId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<AnalysisReport | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Block 3: Audit-Empfehlungen actionable — angewendete Fixes (steuert den
  // Re-Audit-Button), Didaktik-Chat im Kurs-Kontext, Re-Audit-State.
  const [appliedFixes, setAppliedFixes] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSeed, setChatSeed] = useState("");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatProposal, setChatProposal] = useState<ChatProposal | null>(null);
  const [chatApplyErrors, setChatApplyErrors] = useState<string[]>([]);
  const [reauditBusy, setReauditBusy] = useState(false);
  const [reauditError, setReauditError] = useState<string | null>(null);
  const [quota, setQuota] = useState<unknown>(null);
  const chatRef = useRef<HTMLDivElement>(null);

  const dateLocale = i18n.language.startsWith("en") ? "en-GB" : "de-DE";

  useEffect(() => {
    if (!id || !reportId) return;
    // Report-Wechsel (z. B. nach Re-Audit): Block-3-State zurücksetzen —
    // angewendete Fixes und Chat gehören zum jeweils angezeigten Report.
    setAppliedFixes(0);
    setChatOpen(false);
    setChatSeed("");
    setChatMessages([]);
    setChatProposal(null);
    setChatApplyErrors([]);
    setReauditError(null);
    Promise.all([api.getReport(id, reportId), api.getCourse(id)]).then(([r, c]) => {
      setReport(r);
      setCourse(c);
    });
  }, [id, reportId]);

  if (!report || !course) return <LoadingOverlay message={t("audit.loading")} />;

  // Kurs nach einem angewendeten Fix neu laden (Vorschau zeigt echten Stand).
  const refetchCourse = async () => {
    if (!id) return;
    setCourse(await api.getCourse(id));
  };

  const onFixApplied = () => {
    setAppliedFixes((c) => c + 1);
    void refetchCourse();
  };

  // „Mit der KI besprechen": Chat im Kurs-Kontext öffnen, vorbefüllt mit
  // Befund + Empfehlung des Flaws.
  const onDiscussFlaw = (flaw: DetectedFlaw) => {
    const seed = `${flaw.description ?? ""}${
      flaw.recommendation
        ? `\n\n${t("audit.recommendation")}: ${flaw.recommendation}`
        : ""
    }`;
    setChatSeed(seed);
    setChatOpen(true);
    setTimeout(
      () => chatRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      50,
    );
  };

  // Chat-Proposal im Kurs-Kontext anwenden: Items mit id = PATCH auf den
  // Bestand, Items ohne id = Neuanlage. Entfernt wird nie etwas (das Löschen
  // bleibt dem Editor vorbehalten — Hinweis chat.applyHintCourse).
  const applyCourseChatProposal = async (p: ChatProposal) => {
    const errs: string[] = [];
    for (const item of p.items) {
      try {
        if (p.target === "ilos") {
          const it = item as SuggestedILO;
          const payload = {
            text: it.text,
            action_verb: it.action_verb,
            bender_depth: it.bender_depth,
          };
          if (it.id) await api.updateILO(course.id, it.id, payload);
          else await api.createILO(course.id, payload);
        } else if (p.target === "tlas") {
          const it = item as SuggestedTLA;
          const payload = {
            type: it.type,
            description: it.description,
            duration_minutes: it.duration_minutes ?? undefined,
            modality: it.modality ?? undefined,
            linked_ilo_ids: it.linked_ilo_ids ?? [],
          };
          if (it.id) await api.updateTLA(course.id, it.id, payload);
          else await api.createTLA(course.id, payload);
        } else {
          const it = item as SuggestedAT;
          const payload = {
            type: it.type,
            description: it.description,
            format: it.format ?? undefined,
            duration_minutes: it.duration_minutes ?? undefined,
            linked_ilo_ids: it.linked_ilo_ids ?? [],
          };
          if (it.id) await api.updateAT(course.id, it.id, payload);
          else await api.createAT(course.id, payload);
        }
      } catch (e) {
        errs.push(e instanceof APIError ? e.detail : String(e));
      }
    }
    setChatProposal(null);
    setChatApplyErrors(errs);
    await refetchCourse();
    setAppliedFixes((c) => c + 1);
  };

  // Re-Audit nach ≥1 angewendetem Fix: expliziter Confirm (1 Credit),
  // 402/422 verständlich anzeigen. Der alte Report bleibt in der Historie.
  const handleReaudit = async () => {
    if (!window.confirm(t("audit.reAuditConfirm"))) return;
    setReauditBusy(true);
    setReauditError(null);
    try {
      const newReport = await api.runAudit(course.id);
      navigate(`/courses/${course.id}/audit/${newReport.id}`);
    } catch (err) {
      if (err instanceof APIError && err.status === 422 && isFeasibilityDetail(err.data)) {
        setReauditError(err.data.message);
      } else if (isQuotaError(err)) {
        setQuota(err.data);
      } else if (err instanceof APIError) {
        setReauditError(err.detail);
      } else {
        setReauditError(t("audit.reAuditFailed"));
      }
    } finally {
      setReauditBusy(false);
    }
  };

  const handleDownload = async () => {
    setExportError(null);
    setExporting(true);
    try {
      await downloadPdf(course.id, report.id, course.title);
    } catch (err) {
      setExportError(
        err instanceof Error && /^\d+$/.test(err.message)
          ? t("audit.exportFailedHttp", { status: err.message })
          : t("audit.exportFailed"),
      );
    } finally {
      setExporting(false);
    }
  };

  const score = report.overall_score;
  const hasScore = typeof score === "number" && Number.isFinite(score)
    && score >= 0 && score <= 100 && report.model_used !== "heuristic-fallback";
  const level = report.performance_level ?? 1;
  const ilosById = Object.fromEntries((course.ilos || []).map((i) => [i.id, i]));
  // Das LLM schreibt die 1-basierte Lernziel-NUMMER ins ilo_id-Feld (by design,
  // siehe full_analysis.py) — der Web-Report muss wie pdf.py über die Position
  // zurückmappen. UUID-Fallback für Alt-Berichte.
  const ilosByNumber = Object.fromEntries(
    [...(course.ilos || [])]
      .sort((a, b) => a.position - b.position)
      .map((i, n) => [String(n + 1), i]),
  );

  return (
    <>
      <Header />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <section className="bg-ca-card border border-ca-surface-border rounded-xl overflow-hidden animate-fade-in">
          {/* Header */}
          <div className="p-10 bg-ca-card border-b border-ca-surface-border relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-ca-accent/5 blur-[80px] -mr-32 -mt-32" />
            <h1 className="text-5xl font-black text-ca-surface-ink mb-4 tracking-tighter uppercase italic">
              {t("audit.title")}
            </h1>
            <div className="flex flex-wrap gap-4 text-xs font-black tracking-widest uppercase">
              <span className="bg-ca-accent text-ca-brand px-4 py-2 rounded-full shadow-lg">
                {course.title}
              </span>
              {course.audience && (
                <span className="bg-white text-ca-surface-ink px-4 py-2 rounded-full border border-ca-surface-border">
                  {course.audience.length > 60
                    ? course.audience.slice(0, 60) + "…"
                    : course.audience}
                </span>
              )}
              <span className="ml-auto text-ca-surface-muted">
                {new Date(report.generated_at).toLocaleDateString(dateLocale)}
              </span>
            </div>
            {Boolean(course.metadata?.rough_idea) && (
              <p className="mt-4 text-sm text-ca-surface-muted leading-relaxed whitespace-pre-wrap">
                {String(course.metadata?.rough_idea)}
              </p>
            )}
          </div>

          <div className="px-10 pb-12">
            {report.model_used === "heuristic-fallback" && (
              <div className="mt-8 p-6 rounded-2xl bg-verdict-orange-bg border-2 border-verdict-orange text-ca-surface-ink">
                <p className="font-black uppercase text-sm tracking-widest mb-2">
                  {t("audit.fallbackTitle")}
                </p>
                <p className="text-sm leading-relaxed">
                  {t("audit.fallbackBody")}
                </p>
              </div>
            )}
            {/* Staleness-Guard (Plan-Gate-Auflage): Kurs nach dem Audit
                geändert → deutlicher Hinweis, Befunde beziehen sich auf den
                älteren Stand. */}
            {new Date(course.updated_at) > new Date(report.generated_at) && (
              <div className="mt-8 p-6 rounded-2xl bg-verdict-orange-bg border-2 border-verdict-orange text-ca-surface-ink">
                <p className="text-sm font-bold leading-relaxed">
                  {t("audit.staleBanner")}
                </p>
              </div>
            )}
            {/* Score */}
            <div className="flex items-center justify-between my-12">
              <h2 className="text-4xl font-black text-ca-surface-ink uppercase italic tracking-tighter">
                {t("audit.overall")}
              </h2>
              <div className="text-center bg-white p-6 rounded-3xl border-2 border-ca-accent/20 min-w-[160px]">
                <div
                  className={`text-6xl font-black tracking-tighter ${
                    hasScore && score > 70 ? "text-ca-surface-accent" : "text-orange-500"
                  }`}
                >
                  {hasScore ? `${Math.round(score)}%` : "—"}
                </div>
                <div className="text-[10px] font-black uppercase text-ca-surface-muted tracking-[0.3em] mt-1">
                  Alignment Score
                </div>
              </div>
            </div>
            <p className="text-sm text-ca-surface-muted leading-relaxed -mt-6 mb-12 max-w-2xl">
              {hasScore
                ? t("audit.designScoreHint", { defaultValue: dateLocale.startsWith("de")
                    ? "KI-Einschätzung des didaktischen Designs. Der Wert ist kein Nachweis tatsächlicher Lern- oder Transferwirkung."
                    : "AI assessment of the instructional design. This score does not demonstrate actual learning or transfer outcomes." })
                : t("audit.noScoreHint", { defaultValue: dateLocale.startsWith("de")
                    ? "Keine belastbare Gesamtbewertung verfügbar. Bitte den Audit erneut ausführen."
                    : "No valid overall assessment is available. Please run the audit again." })}
            </p>

            {/* Bender-Performanz-Bar */}
            <div className="mb-12 p-10 bg-white rounded-xl border border-ca-surface-border">
              <h3 className="text-ca-surface-accent font-black uppercase text-xs mb-8 tracking-[0.3em] opacity-80">
                {t("audit.depthTitle")}
              </h3>
              <div className="flex justify-between items-center mb-6 gap-3">
                {BENDER_LABELS.map((label, i) => (
                  <div key={label} className="flex-1 flex flex-col items-center gap-3">
                    <div
                      className={`h-3 w-full rounded-full transition-all ${
                        level >= i + 1
                          ? "bg-ca-accent"
                          : "bg-ca-surface-border"
                      }`}
                    />
                    <div
                      className={`text-[10px] font-black uppercase transition ${
                        level === i + 1 ? "text-ca-surface-accent" : "text-ca-surface-muted"
                      }`}
                    >
                      {label}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-ca-surface-muted text-sm">
                {t("audit.reachedLevel")} <b className="text-ca-surface-accent">{report.bender_label}</b>
              </p>
            </div>

            {/* Summary */}
            {report.summary && (
              <div className="p-8 bg-ca-accent rounded-xl mb-12 italic text-ca-brand leading-relaxed text-lg font-bold">
                „{report.summary}"
              </div>
            )}

            {/* Per-ILO-Analyse */}
            {report.per_ilo_analysis && report.per_ilo_analysis.length > 0 && (
              <div className="space-y-8 mb-16">
                <h3 className="text-3xl font-black text-ca-surface-ink flex items-center gap-4 italic uppercase">
                  <span className="w-3 h-10 bg-ca-accent rounded-full" />
                  {t("audit.perIloTitle")}
                </h3>
                {report.per_ilo_analysis.map((analysis: PerILOAnalysis, idx: number) => {
                  const ilo = ilosByNumber[analysis.ilo_id] ?? ilosById[analysis.ilo_id];
                  return (
                    <div
                      key={idx}
                      className={`p-10 rounded-3xl border-2 ${
                        analysis.is_aligned
                          ? "bg-white border-ca-accent/20"
                          : "bg-white border-orange-300"
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-6 flex-wrap">
                        <span
                          className={`px-5 py-2 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] ${
                            analysis.is_aligned
                              ? "bg-ca-accent text-ca-brand"
                              : "bg-orange-600 text-ca-ink"
                          }`}
                        >
                          {analysis.is_aligned ? t("audit.alignedOk") : t("audit.alignedTension")}
                        </span>
                        {analysis.bender_depth_observed && (
                          <span className="text-xs font-black text-ca-surface-accent uppercase bg-ca-card px-4 py-2 rounded-xl border border-ca-surface-border tracking-widest">
                            {analysis.bender_depth_observed}
                          </span>
                        )}
                        {ilo?.action_verb && (
                          <span className="text-xs font-black text-ca-surface-muted uppercase bg-ca-card px-4 py-2 rounded-xl tracking-widest">
                            {ilo.action_verb}
                          </span>
                        )}
                      </div>
                      {ilo && (
                        <p className="text-xl font-black text-ca-surface-ink mb-8 leading-snug">
                          „{ilo.text}"
                        </p>
                      )}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="p-6 bg-ca-card rounded-2xl border border-ca-surface-border">
                          <strong className="block text-ca-surface-muted uppercase text-[10px] mb-3 tracking-[0.3em] font-black">
                            {t("audit.finding")}
                          </strong>
                          <p className="text-sm text-ca-surface-ink leading-relaxed">
                            {analysis.critique}
                          </p>
                        </div>
                        <div className="p-6 bg-ca-card text-ca-surface-ink rounded-2xl border border-ca-surface-border relative">
                          <div className="absolute top-0 right-6 w-10 h-1 bg-ca-accent" />
                          <strong className="block text-ca-surface-muted uppercase text-[10px] mb-3 tracking-[0.3em] font-black">
                            {t("audit.recommendation")}
                          </strong>
                          <p className="text-sm font-medium leading-relaxed">
                            {analysis.improvement}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Strukturelle Misalignments (über einzelne ILOs hinaus) */}
            {report.detected_flaws && report.detected_flaws.length > 0 && (
              <div className="space-y-6 mb-16">
                <h3 className="text-3xl font-black text-ca-surface-ink flex items-center gap-4 italic uppercase">
                  <span className="w-3 h-10 bg-orange-500 rounded-full shadow-[0_0_15px_rgba(249,115,22,0.5)]" />
                  {t("audit.structuralTitle")}
                </h3>
                {report.detected_flaws.map((flaw: DetectedFlaw, idx: number) => {
                  const isCritical = flaw.severity === "critical";
                  return (
                    <div
                      key={idx}
                      className={`p-8 rounded-3xl border-2 ${
                        isCritical
                          ? "bg-white border-red-300"
                          : "bg-white border-ca-accent/20"
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <span
                          className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] ${
                            isCritical ? "bg-red-600 text-ca-ink" : "bg-ca-accent text-ca-brand"
                          }`}
                        >
                          {flaw.severity || "info"}
                        </span>
                        {flaw.flaw_type && (
                          <span className="text-xs font-black text-ca-surface-muted uppercase tracking-widest">
                            {flaw.flaw_type.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                      <p className="text-base text-ca-surface-ink mb-4 leading-relaxed">
                        {flaw.description}
                      </p>
                      {flaw.recommendation && (
                        <div className="p-5 bg-ca-card rounded-2xl border border-ca-surface-border">
                          <strong className="block text-ca-surface-muted uppercase text-[10px] mb-2 tracking-[0.3em] font-black">
                            {t("audit.recommendation")}
                          </strong>
                          <p className="text-sm text-ca-surface-ink leading-relaxed">
                            {flaw.recommendation}
                          </p>
                        </div>
                      )}
                      {/* Block 3: Empfehlung actionable — Einbauen /
                          Modifizieren / Besprechen */}
                      <FlawFix
                        courseId={course.id}
                        reportId={report.id}
                        flawIndex={idx}
                        iloIds={(course.ilos || []).map((i) => i.id)}
                        onApplied={onFixApplied}
                        onDiscuss={() => onDiscussFlaw(flaw)}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Re-Audit nach ≥1 angewendetem Fix (1 Credit, mit Confirm) */}
            {appliedFixes >= 1 && (
              <div className="mb-16 p-8 rounded-3xl border-2 border-ca-accent/40 bg-ca-accent/5">
                <p className="text-sm text-ca-surface-ink leading-relaxed mb-4">
                  {t("audit.reAuditHint")}
                </p>
                <button
                  type="button"
                  onClick={() => void handleReaudit()}
                  disabled={reauditBusy}
                  className="px-6 py-3 bg-ca-accent text-ca-brand text-sm font-black uppercase tracking-widest rounded-xl shadow-lg hover:bg-brand-gold-dark transition disabled:opacity-40"
                >
                  {reauditBusy ? t("audit.reAuditBusy") : t("audit.reAudit")}
                </button>
                <span className="ml-3 text-xs">
                  <CreditCost feature="audit" />
                </span>
                {quota !== null && (
                  <div className="mt-4">
                    <QuotaExhaustedCard detail={quota} onDismiss={() => setQuota(null)} />
                  </div>
                )}
                {reauditError && (
                  <p className="mt-3 text-sm text-verdict-red">{reauditError}</p>
                )}
              </div>
            )}

            {/* Theoretischer Impuls */}
            {report.theoretical_insight && (
              <div className="bg-white p-12 rounded-xl text-ca-surface-ink border border-ca-surface-border relative overflow-hidden mb-12">
                <div className="absolute top-0 right-0 w-96 h-96 bg-ca-accent/40 rounded-full blur-[120px] -mr-48 -mt-48" />
                <div className="relative z-10">
                  <h4 className="text-ca-surface-muted text-[10px] font-black uppercase mb-6 tracking-[0.4em]">
                    {t("audit.theoryTitle")}
                  </h4>
                  <p className="text-2xl italic font-black leading-tight">
                    „{report.theoretical_insight}"
                  </p>
                </div>
              </div>
            )}

            {/* Didaktik-Chat im Kurs-Kontext (Block 3, „Mit der KI
                besprechen") — der Server lädt den Kurs selbst, Vorschläge
                werden per PATCH/POST in den Kurs geschrieben. */}
            {chatOpen && (
              <div ref={chatRef}>
                <DidaktikChat
                  context="course"
                  courseId={course.id}
                  seedInput={chatSeed}
                  language={
                    (course.metadata?.content_lang as ContentLang | undefined) ?? "de"
                  }
                  messages={chatMessages}
                  setMessages={setChatMessages}
                  proposal={chatProposal}
                  setProposal={setChatProposal}
                  onApply={(p) => void applyCourseChatProposal(p)}
                />
                {chatApplyErrors.length > 0 && (
                  <div className="mt-2 p-4 rounded-xl bg-verdict-red-bg border border-verdict-red/60">
                    <p className="text-xs font-bold text-verdict-red mb-1">
                      {t("flawfix.applyErrors")}
                    </p>
                    <ul className="text-xs text-verdict-red space-y-1">
                      {chatApplyErrors.map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="p-10 bg-ca-card border-t border-ca-surface-border space-y-4">
            <button
              onClick={handleDownload}
              disabled={exporting}
              className="block w-full py-6 bg-ca-accent text-ca-brand text-center text-xl font-black uppercase tracking-widest rounded-2xl transition active:scale-95 disabled:opacity-60"
            >
              {exporting ? t("audit.exporting") : t("audit.exportPdf")}
            </button>
            {exportError && (
              <p className="text-red-400 text-sm text-center">{exportError}</p>
            )}
            <button
              onClick={() => navigate(`/courses/${course.id}`)}
              className="w-full py-4 text-ca-surface-muted font-black text-sm uppercase tracking-widest rounded-2xl border border-transparent hover:border-ca-surface-border hover:text-ca-surface-ink transition"
            >
              {t("audit.backToOverview")}
            </button>
          </div>
        </section>
      </div>
    </>
  );
}
